import { useMemo, useState } from "react";
import { COLOR, FONT, RADIUS } from "../lib/tokens";
import { useTechniques } from "../lib/useTechniques";
import { uniqueTechniquesInProse } from "../lib/matchTechniqueInProse";
import TechniqueSheet from "./TechniqueSheet";

// TechniqueChips — small row beneath a recipe step that surfaces
// detected techniques as tappable chips.
//
// Detection runs against the step's instruction prose via the alias
// matcher in src/lib/matchTechniqueInProse.js. Each unique technique
// renders one chip; tap opens the TechniqueSheet with the row's full
// explainer (long description, doneness cues, pitfalls, params).
//
// Renders nothing when no techniques are detected — silent fallback,
// not a "no techniques here" empty state. That keeps clean steps
// visually clean.
//
// Egress: zero per render. The catalog hits Supabase at most once
// per 7-day TTL (useTechniques → useCachedQuery). Matching itself is
// a synchronous string scan.

export default function TechniqueChips({ prose }) {
  const { techniques, bySlug, loading } = useTechniques();
  const [openSlug, setOpenSlug] = useState(null);

  const detected = useMemo(
    () => loading ? [] : uniqueTechniquesInProse(prose || "", techniques),
    [prose, techniques, loading],
  );

  if (detected.length === 0) return null;

  return (
    <>
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 6,
        marginTop: 14,
        opacity: 0,
        animation: "stepFadeUp 520ms 900ms cubic-bezier(0.22, 1, 0.36, 1) forwards",
      }}>
        <span style={{
          fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
          color: COLOR.muted, letterSpacing: "0.14em",
          alignSelf: "center", marginRight: 4,
        }}>
          LEARN
        </span>
        {detected.map(({ slug, name }) => (
          <button
            key={slug}
            type="button"
            onClick={() => setOpenSlug(slug)}
            className="mcm-focusable"
            style={{
              appearance: "none",
              fontFamily: FONT.forma, fontSize: 13, fontWeight: 500,
              color: COLOR.ink,
              background: COLOR.deep,
              border: `1px solid ${COLOR.border}`,
              borderRadius: RADIUS.pill,
              padding: "5px 11px",
              cursor: "pointer",
              touchAction: "manipulation",
              display: "inline-flex", alignItems: "center", gap: 5,
            }}
          >
            <span>{name}</span>
            <span style={{
              fontFamily: FONT.forma, fontSize: 12,
              color: COLOR.sub, lineHeight: 1,
              marginTop: 1,
            }}>
              →
            </span>
          </button>
        ))}
      </div>

      {openSlug && (
        <TechniqueSheet
          slug={openSlug}
          catalog={techniques}
          bySlug={bySlug}
          onClose={() => setOpenSlug(null)}
        />
      )}
    </>
  );
}
